import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import ImageExt from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Quote,
  Link as LinkIcon, Image as ImageIcon, Minus, Undo, Redo,
  RemoveFormatting, Code2, Eye, Loader2, Upload,
} from 'lucide-react';
import { toast } from 'sonner';

const ToolbarButton = ({ onClick, active, disabled, title, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-2 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      active ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'
    }`}
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange, token }) => {
  const [sourceMode, setSourceMode] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState(value || '');
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      ImageExt.configure({ inline: false, HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Write your article...' }),
    ],
    content: value || '',
    shouldRerenderOnTransaction: true,
    immediatelyRender: true,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: { class: 'tiptap-content prose prose-slate max-w-none focus:outline-none min-h-[320px] px-4 py-3' },
    },
  });

  const toggleSource = () => {
    if (!sourceMode) {
      setHtmlDraft(editor?.getHTML() || value || '');
      setSourceMode(true);
    } else {
      onChange(htmlDraft);
      editor?.commands.setContent(htmlDraft || '', { emitUpdate: false });
      setSourceMode(false);
    }
  };

  const setLink = useCallback(() => {
    const prevUrl = editor?.getAttributes('link').href || '';
    const url = window.prompt('Enter URL', prevUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const insertImageUrl = useCallback(() => {
    const url = window.prompt('Image URL');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const uploadAndInsertImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Please upload JPG, PNG, WebP, or GIF image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };
      const res = await axios.post(`${API}/blogs/upload-cover`, fd, { headers, withCredentials: true, timeout: 30000 });
      const src = res.data.url.startsWith('/api') ? `${API.replace('/api', '')}${res.data.url}` : res.data.url;
      editor.chain().focus().setImage({ src }).run();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    }
    setUploadingImg(false);
    if (imgInputRef.current) imgInputRef.current.value = '';
  };

  useEffect(() => {
    if (!editor || sourceMode) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor, sourceMode]);

  if (!editor) return null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="rich-text-editor">
      <input
        ref={imgInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={uploadAndInsertImage}
        className="hidden"
        data-testid="content-image-file-input"
      />
      <div className="flex items-center gap-0.5 flex-wrap p-2 border-b border-slate-200 bg-slate-50">
        <ToolbarButton testId="fmt-h2" title="Heading 2" active={editor.isActive('heading', { level: 2 })} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <span className="font-bold text-xs w-4 text-center inline-block">H2</span>
        </ToolbarButton>
        <ToolbarButton testId="fmt-h3" title="Heading 3" active={editor.isActive('heading', { level: 3 })} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <span className="font-bold text-xs w-4 text-center inline-block">H3</span>
        </ToolbarButton>
        <ToolbarButton testId="fmt-paragraph" title="Paragraph" active={editor.isActive('paragraph')} disabled={sourceMode}
          onClick={() => editor.chain().focus().setParagraph().run()}>
          <span className="font-bold text-xs w-4 text-center inline-block">P</span>
        </ToolbarButton>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <ToolbarButton testId="fmt-bold" title="Bold" active={editor.isActive('bold')} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId="fmt-italic" title="Italic" active={editor.isActive('italic')} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId="fmt-underline" title="Underline" active={editor.isActive('underline')} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId="fmt-strike" title="Strikethrough" active={editor.isActive('strike')} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <ToolbarButton testId="fmt-align-left" title="Align left" active={editor.isActive({ textAlign: 'left' })} disabled={sourceMode}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}><AlignLeft className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId="fmt-align-center" title="Align center" active={editor.isActive({ textAlign: 'center' })} disabled={sourceMode}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}><AlignCenter className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId="fmt-align-right" title="Align right" active={editor.isActive({ textAlign: 'right' })} disabled={sourceMode}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}><AlignRight className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <ToolbarButton testId="fmt-bullet-list" title="Bulleted list" active={editor.isActive('bulletList')} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId="fmt-ordered-list" title="Numbered list" active={editor.isActive('orderedList')} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId="fmt-blockquote" title="Blockquote" active={editor.isActive('blockquote')} disabled={sourceMode}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <ToolbarButton testId="fmt-link" title="Insert link" active={editor.isActive('link')} disabled={sourceMode} onClick={setLink}>
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testId="fmt-image-url" title="Insert image from URL" disabled={sourceMode} onClick={insertImageUrl}>
          <ImageIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testId="fmt-image-upload" title="Upload image" disabled={sourceMode || uploadingImg} onClick={() => imgInputRef.current?.click()}>
          {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        </ToolbarButton>
        <ToolbarButton testId="fmt-divider" title="Divider" disabled={sourceMode}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <ToolbarButton testId="fmt-undo" title="Undo" disabled={sourceMode} onClick={() => editor.chain().focus().undo().run()}>
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testId="fmt-redo" title="Redo" disabled={sourceMode} onClick={() => editor.chain().focus().redo().run()}>
          <Redo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testId="fmt-clear" title="Clear formatting" disabled={sourceMode}
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>
          <RemoveFormatting className="w-4 h-4" />
        </ToolbarButton>
        <div className="flex-1" />
        <Button
          type="button"
          size="sm"
          variant={sourceMode ? 'default' : 'outline'}
          onClick={toggleSource}
          data-testid="fmt-source-toggle"
          className="gap-1.5"
        >
          {sourceMode ? <><Eye className="w-3.5 h-3.5" />Preview</> : <><Code2 className="w-3.5 h-3.5" />HTML</>}
        </Button>
      </div>

      {sourceMode ? (
        <textarea
          value={htmlDraft}
          onChange={(e) => setHtmlDraft(e.target.value)}
          rows={16}
          className="w-full px-4 py-3 font-mono text-sm focus:outline-none resize-none"
          placeholder="<p>Edit raw HTML here...</p>"
          data-testid="fmt-source-textarea"
        />
      ) : (
        <EditorContent editor={editor} data-testid="fmt-editor-content" />
      )}
    </div>
  );
};

export default RichTextEditor;
